import { SettingsBackupRestore, ZoomIn, ZoomOut } from '@mui/icons-material';
import { IconButton, Skeleton } from '@mui/material';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { type ReactZoomPanPinchState, TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';

import { IMAGE_ZOOM_SCALE } from 'src/constants';
import { useStyles } from './styles';

interface ImagePreviewProps {
  src: string;
  nextSrc?: string | null;
  prevSrc?: string | null;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ src, nextSrc, prevSrc }) => {
  const classes = useStyles();
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  const [currentImage, setCurrentImage] = useState<HTMLImageElement | null>(null);
  // Use a ref for the preload cache to avoid re-render cascades.
  // Previously this was useState which caused the useEffect to re-fire
  // every time a preloaded image completed (because the Map was in the dep array).
  const preloadCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

  useEffect(() => {
    let cancelled = false;
    const cache = preloadCacheRef.current;

    const preloadImage = (imageSrc: string | null | undefined): Promise<HTMLImageElement | undefined> => {
      if (!imageSrc) return Promise.resolve(undefined);
      const cached = cache.get(imageSrc);
      if (cached) return Promise.resolve(cached);

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          cache.set(imageSrc, img);
          resolve(img);
        };
        img.onerror = () => resolve(undefined);
        img.src = imageSrc;
      });
    };

    // Load current image — keep old image visible until new one is ready
    preloadImage(src).then((img) => {
      if (!cancelled && img) {
        setCurrentImage(img);
        setIsImageLoaded(true);
      }
    });

    // Preload adjacent images in the background (no state updates needed)
    if (nextSrc) preloadImage(nextSrc);
    if (prevSrc) preloadImage(prevSrc);

    return () => {
      cancelled = true;
    };
  }, [src, nextSrc, prevSrc]);

  return (
    <TransformWrapper initialScale={1} minScale={IMAGE_ZOOM_SCALE.MIN} centerOnInit={true} centerZoomedOut={false}>
      {({
        zoomIn,
        zoomOut,
        resetTransform,
        state,
      }: {
        zoomIn: () => void;
        zoomOut: () => void;
        resetTransform: () => void;
        state: ReactZoomPanPinchState;
      }) => {
        return (
          <div className={classes.mainSection}>
            <div className={classes.tools}>
              <IconButton disabled={state.scale === IMAGE_ZOOM_SCALE.MAX} onClick={() => zoomIn()} size="large">
                <ZoomIn />
              </IconButton>
              <IconButton disabled={state.scale === IMAGE_ZOOM_SCALE.MIN} onClick={() => zoomOut()} size="large">
                <ZoomOut />
              </IconButton>
              <IconButton disabled={state.scale === 1} onClick={() => resetTransform()} size="large">
                <SettingsBackupRestore />
              </IconButton>
            </div>
            <TransformComponent
              contentStyle={{
                width: '100%',
                height: '100%',
                margin: 'auto',
                //transformOrigin: state.scale === INITIAL_SCALE ? 'center' : '0 0',
                opacity: isImageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out',
              }}
              wrapperStyle={{
                maxWidth: '100vw',
                maxHeight: '90vh',
              }}
            >
              {!isImageLoaded && (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: 400,
                    height: 300,
                    bgcolor: (theme) => theme.palette.action.disabledBackground,
                  }}
                />
              )}

              {currentImage && (
                <img
                  src={currentImage.src}
                  className={classes.modalImage}
                  style={{ opacity: isImageLoaded ? 1 : 0 }}
                  alt="Preview"
                />
              )}
            </TransformComponent>
          </div>
        );
      }}
    </TransformWrapper>
  );
};

export default ImagePreview;
